import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Checkbox, List, ListItem, ListItemText, ListItemIcon, Chip, Typography,
  Divider, CircularProgress, Tabs, Tab, IconButton, Table, TableHead,
  TableBody, TableRow, TableCell
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SaveIcon from "@material-ui/icons/Save";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 14 }}>{value === index && children}</div>
);

const GroupMessageDialog = ({ open, onClose, whatsappId, groups }) => {
  const fileRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState([]);
  const [messageType, setMessageType] = useState("text");
  const [body, setBody] = useState("");
  const [pollText, setPollText] = useState("");
  const [mentionGhost, setMentionGhost] = useState(false);
  const [file, setFile] = useState(null);
  const [mediaType, setMediaType] = useState("image");
  const [templateMediaPath, setTemplateMediaPath] = useState("");
  const [antiBan, setAntiBan] = useState(8);
  const [sendType, setSendType] = useState("once");
  const [scheduledAt, setScheduledAt] = useState("");
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState("days");
  const [startAt, setStartAt] = useState("");
  const [limitType, setLimitType] = useState("none");
  const [endAt, setEndAt] = useState("");
  const [maxRuns, setMaxRuns] = useState(10);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setTab(0);
    loadSchedules(); loadTemplates();
    // eslint-disable-next-line
  }, [open, whatsappId]);

  const loadSchedules = async () => {
    if (!whatsappId) return;
    try { const { data } = await api.get("/group-schedules", { params: { whatsappId } }); setSchedules(data || []); } catch (e) { /* */ }
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/group-templates"); setTemplates(data || []); } catch (e) { /* */ }
  };
  const loadReport = async () => {
    if (!whatsappId) return;
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/group-send-logs/summary", { params: { whatsappId } }),
        api.get("/group-send-logs", { params: { whatsappId, limit: 100 } })
      ]);
      setSummary(s || []); setLogs(l || []);
    } catch (e) { /* */ }
  };

  const toggle = (jid) => setSelected(p => p.includes(jid) ? p.filter(j => j !== jid) : [...p, jid]);
  const allJids = (groups || []).map(g => g.jid);
  const toggleAll = () => setSelected(selected.length === allJids.length ? [] : allJids);

  const clearMedia = () => { setFile(null); setTemplateMediaPath(""); };

  const loadTemplate = (id) => {
    const t = templates.find(x => String(x.id) === String(id));
    if (!t) return;
    setMessageType(t.messageType || "text");
    setBody(t.body || "");
    setPollText((t.pollOptions || []).join("\n"));
    setMentionGhost(!!t.mentionGhost);
    setMediaType(t.mediaType || "image");
    setFile(null);
    setTemplateMediaPath(t.mediaPath || "");
    toast.info(`Template "${t.name}" carregado.`);
  };

  const buildForm = () => {
    const fd = new FormData();
    fd.append("whatsappId", whatsappId);
    fd.append("groupJids", JSON.stringify(selected));
    fd.append("messageType", messageType);
    fd.append("body", body || "");
    fd.append("mentionGhost", mentionGhost ? "true" : "false");
    fd.append("antiBanSeconds", String(antiBan));
    if (messageType === "poll") fd.append("pollOptions", JSON.stringify(pollText.split("\n").map(s => s.trim()).filter(Boolean)));
    if (messageType === "media") {
      fd.append("mediaType", mediaType);
      if (file) { fd.append("file", file); fd.append("typeArch", "groups"); }
      else if (templateMediaPath) { fd.append("mediaPath", templateMediaPath); }
    }
    return fd;
  };

  const validate = () => {
    if (!selected.length) { toast.error("Selecione ao menos um grupo."); return false; }
    if (messageType === "media" && !file && !templateMediaPath) { toast.error("Anexe um arquivo."); return false; }
    if (messageType === "poll" && pollText.split("\n").filter(s => s.trim()).length < 2) { toast.error("Enquete precisa de 2+ opções."); return false; }
    if ((messageType === "text" || messageType === "mention") && !body.trim() && !mentionGhost) { toast.error("Escreva a mensagem."); return false; }
    return true;
  };

  const handleSendNow = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/groups/send-now", buildForm(), { headers: { "Content-Type": "multipart/form-data" } });
      const ok = (data.results || []).filter(r => r.ok).length;
      const fail = (data.results || []).length - ok;
      toast.success(`Enviado: ${ok} ok${fail ? `, ${fail} falha(s)` : ""}.`);
      onClose(true);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const handleSchedule = async () => {
    if (!validate()) return;
    if (sendType === "once" && !scheduledAt) { toast.error("Informe a data/hora."); return; }
    setSaving(true);
    try {
      const fd = buildForm();
      fd.append("sendType", sendType);
      if (sendType === "once") fd.append("scheduledAt", new Date(scheduledAt).toISOString());
      if (sendType === "interval") {
        fd.append("intervalValue", String(intervalValue));
        fd.append("intervalUnit", intervalUnit);
        if (startAt) fd.append("startAt", new Date(startAt).toISOString());
        if (limitType === "end" && endAt) fd.append("endAt", new Date(endAt).toISOString());
        if (limitType === "max") fd.append("maxRuns", String(maxRuns));
      }
      await api.post("/group-schedules", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Agendamento criado."); loadSchedules(); setTab(2);
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Nome do template:");
    if (!name) return;
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("messageType", messageType);
      fd.append("body", body || "");
      fd.append("mentionGhost", mentionGhost ? "true" : "false");
      if (messageType === "poll") fd.append("pollOptions", JSON.stringify(pollText.split("\n").map(s => s.trim()).filter(Boolean)));
      if (messageType === "media") {
        fd.append("mediaType", mediaType);
        if (file) { fd.append("file", file); fd.append("typeArch", "groups"); }
        else if (templateMediaPath) fd.append("mediaPath", templateMediaPath);
      }
      await api.post("/group-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template salvo."); loadTemplates();
    } catch (e) { toastError(e); }
  };
  const delTemplate = async (id) => { try { await api.delete(`/group-templates/${id}`); loadTemplates(); } catch (e) { toastError(e); } };

  const pauseResume = async (s) => { try { await api.put(`/group-schedules/${s.id}/${s.status === "paused" ? "resume" : "pause"}`); loadSchedules(); } catch (e) { toastError(e); } };
  const delSchedule = async (s) => { try { await api.delete(`/group-schedules/${s.id}`); loadSchedules(); } catch (e) { toastError(e); } };

  const templateBar = (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
      <FormControl style={{ minWidth: 200 }} size="small">
        <InputLabel>Carregar template</InputLabel>
        <Select value="" onChange={(e) => loadTemplate(e.target.value)}>
          {templates.length === 0 && <MenuItem disabled value="">Nenhum template</MenuItem>}
          {templates.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.name} <span style={{ color: "#999", marginLeft: 6 }}>({t.messageType})</span>
              <DeleteIcon fontSize="small" style={{ marginLeft: "auto", opacity: .5 }} onClick={(ev) => { ev.stopPropagation(); delTemplate(t.id); }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button size="small" startIcon={<SaveIcon />} onClick={saveTemplate}>Salvar como template</Button>
    </div>
  );

  const contentFields = (
    <>
      {templateBar}
      <FormControl fullWidth margin="dense">
        <InputLabel>Tipo de conteúdo</InputLabel>
        <Select value={messageType} onChange={(e) => { setMessageType(e.target.value); }}>
          <MenuItem value="text">Texto</MenuItem>
          <MenuItem value="media">Mídia (imagem/vídeo/PDF/áudio)</MenuItem>
          <MenuItem value="poll">Enquete</MenuItem>
          <MenuItem value="mention">Menção fantasma (marcar todos)</MenuItem>
        </Select>
      </FormControl>

      {messageType === "poll" ? (
        <>
          <TextField fullWidth margin="dense" label="Pergunta da enquete" value={body} onChange={(e) => setBody(e.target.value)} />
          <TextField fullWidth margin="dense" label="Opções (uma por linha)" multiline minRows={3} value={pollText} onChange={(e) => setPollText(e.target.value)} />
        </>
      ) : (
        <TextField fullWidth margin="dense" label={messageType === "mention" ? "Mensagem (marca todos sem @)" : "Mensagem"} placeholder="Use {grupo} e {data}" multiline minRows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      )}

      {messageType === "media" && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => { setFile(e.target.files?.[0] || null); setTemplateMediaPath(""); }} />
          <Button variant="outlined" onClick={() => fileRef.current?.click()}>Anexar arquivo</Button>
          {file && <Chip label={file.name} onDelete={clearMedia} />}
          {!file && templateMediaPath && <Chip label={`mídia do template: ${templateMediaPath.split("/").pop()}`} onDelete={clearMedia} />}
          <FormControl style={{ minWidth: 130 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
              <MenuItem value="image">Imagem</MenuItem>
              <MenuItem value="video">Vídeo</MenuItem>
              <MenuItem value="audio">Áudio</MenuItem>
              <MenuItem value="document">Documento/PDF</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}

      {messageType !== "mention" && (
        <FormControlLabel control={<Switch checked={mentionGhost} onChange={(e) => setMentionGhost(e.target.checked)} color="primary" />} label="Também marcar todos (menção fantasma)" />
      )}

      <TextField margin="dense" label="Intervalo anti-ban (segundos entre grupos)" type="number" value={antiBan} onChange={(e) => setAntiBan(e.target.value)} inputProps={{ min: 0, max: 120 }} style={{ width: 260 }} />
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
        ⚠️ Envios em massa e menção de todos têm risco de bloqueio do número. Use com moderação.
      </Typography>
    </>
  );

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>Mensagens & Agendamentos de Grupos</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === 3) loadReport(); }} indicatorColor="primary" textColor="primary" variant="scrollable">
          <Tab label="Enviar agora" />
          <Tab label="Agendar" />
          <Tab label={`Agendamentos (${schedules.length})`} />
          <Tab label="Relatório" />
        </Tabs>

        {(tab === 0 || tab === 1) && (
          <div style={{ marginTop: 10 }}>
            <Typography variant="subtitle2">Grupos ({selected.length} selecionados)</Typography>
            <Button size="small" onClick={toggleAll}>{selected.length === allJids.length ? "Limpar" : "Selecionar todos"}</Button>
            <List dense style={{ maxHeight: 160, overflow: "auto", border: "1px solid #eee", borderRadius: 8, marginBottom: 10 }}>
              {(groups || []).map((g) => (
                <ListItem key={g.jid} button onClick={() => toggle(g.jid)}>
                  <ListItemIcon><Checkbox edge="start" checked={selected.includes(g.jid)} tabIndex={-1} /></ListItemIcon>
                  <ListItemText primary={g.subject || g.jid} secondary={`${g.participantsCount || 0} membros`} />
                </ListItem>
              ))}
            </List>
            {contentFields}
          </div>
        )}

        <TabPanel value={tab} index={1}>
          <Divider style={{ margin: "10px 0" }} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Quando</InputLabel>
            <Select value={sendType} onChange={(e) => setSendType(e.target.value)}>
              <MenuItem value="once">Uma vez (data/hora)</MenuItem>
              <MenuItem value="interval">Recorrente (a cada X)</MenuItem>
            </Select>
          </FormControl>
          {sendType !== "interval" ? (
            <TextField fullWidth margin="dense" label="Data e hora" type="datetime-local" InputLabelProps={{ shrink: true }} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <TextField label="A cada" type="number" value={intervalValue} onChange={(e) => setIntervalValue(e.target.value)} inputProps={{ min: 1 }} style={{ width: 120 }} />
                <FormControl style={{ minWidth: 130 }}><InputLabel>Unidade</InputLabel>
                  <Select value={intervalUnit} onChange={(e) => setIntervalUnit(e.target.value)}>
                    <MenuItem value="hours">Horas</MenuItem>
                    <MenuItem value="days">Dias</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Começar em" type="datetime-local" InputLabelProps={{ shrink: true }} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
              </div>
              <FormControl fullWidth margin="dense">
                <InputLabel>Limite</InputLabel>
                <Select value={limitType} onChange={(e) => setLimitType(e.target.value)}>
                  <MenuItem value="none">Sem limite (até eu pausar)</MenuItem>
                  <MenuItem value="end">Até uma data</MenuItem>
                  <MenuItem value="max">Nº máximo de envios</MenuItem>
                </Select>
              </FormControl>
              {limitType === "end" && <TextField fullWidth margin="dense" label="Terminar em" type="datetime-local" InputLabelProps={{ shrink: true }} value={endAt} onChange={(e) => setEndAt(e.target.value)} />}
              {limitType === "max" && <TextField margin="dense" label="Máximo de envios" type="number" value={maxRuns} onChange={(e) => setMaxRuns(e.target.value)} inputProps={{ min: 1 }} style={{ width: 200 }} />}
            </>
          )}
        </TabPanel>

        <TabPanel value={tab} index={2}>
          {schedules.length === 0 ? <Typography color="textSecondary" style={{ padding: 12 }}>Nenhum agendamento.</Typography> : (
            <List>
              {schedules.map((s) => (
                <ListItem key={s.id} divider>
                  <ListItemText
                    primary={`${s.name || s.messageType} · ${(s.groupJids || []).length} grupo(s) · ${s.sendType}`}
                    secondary={`status: ${s.status}${s.nextRunAt ? ` · próximo: ${new Date(s.nextRunAt).toLocaleString("pt-BR")}` : ""}${s.lastError ? ` · erro: ${s.lastError}` : ""}`} />
                  {s.status !== "done" && <IconButton onClick={() => pauseResume(s)} title={s.status === "paused" ? "Retomar" : "Pausar"}>{s.status === "paused" ? <PlayArrowIcon /> : <PauseIcon />}</IconButton>}
                  <IconButton onClick={() => delSchedule(s)} title="Excluir"><DeleteIcon /></IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Typography variant="subtitle2" style={{ marginBottom: 6 }}>Resumo por grupo</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Grupo</TableCell><TableCell align="center">Total</TableCell><TableCell align="center">OK</TableCell><TableCell align="center">Falhas</TableCell><TableCell>Último envio</TableCell></TableRow></TableHead>
            <TableBody>
              {summary.map((r) => (
                <TableRow key={r.groupJid}>
                  <TableCell>{r.groupSubject || r.groupJid}</TableCell>
                  <TableCell align="center">{r.total}</TableCell>
                  <TableCell align="center" style={{ color: "#128C4B" }}>{r.ok}</TableCell>
                  <TableCell align="center" style={{ color: r.fail ? "#c0392b" : "inherit" }}>{r.fail}</TableCell>
                  <TableCell>{r.lastAt ? new Date(r.lastAt).toLocaleString("pt-BR") : "-"}</TableCell>
                </TableRow>
              ))}
              {summary.length === 0 && <TableRow><TableCell colSpan={5} align="center" style={{ color: "#888", padding: 16 }}>Sem envios ainda.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <Typography variant="subtitle2" style={{ margin: "14px 0 6px" }}>Envios recentes</Typography>
          <List dense style={{ maxHeight: 200, overflow: "auto" }}>
            {logs.map((l) => (
              <ListItem key={l.id} divider>
                <ListItemText
                  primary={`${l.ok ? "✅" : "❌"} ${l.groupSubject || l.groupJid} · ${l.messageType}`}
                  secondary={`${new Date(l.createdAt).toLocaleString("pt-BR")}${l.error ? ` · ${l.error}` : ""}`} />
              </ListItem>
            ))}
          </List>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Fechar</Button>
        {tab === 0 && <Button variant="contained" color="primary" onClick={handleSendNow} disabled={saving}>{saving ? <CircularProgress size={20} /> : "Enviar agora"}</Button>}
        {tab === 1 && <Button variant="contained" color="primary" onClick={handleSchedule} disabled={saving}>{saving ? <CircularProgress size={20} /> : "Agendar"}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default GroupMessageDialog;
